import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';
import { toTreatmentPlanV3EducationInfoRequest, toTreatmentPlanV3EducationPage } from './utils';

import type {
  ITreatmentPlanV3EducationPage,
  GetTreatmentPlanV3EducationPagePayload,
  UpdateTreatmentPlanV3EducationInfoPayload,
  SetTreatmentPlanV3NotAttendingSchoolPayload,
  TreatmentPlanV3EducationPageThunkConfig,
} from './types';

export function createTreatmentPlanV3EducationThunks() {
  return {
    getTreatmentPlanV3EducationPage: getTreatmentPlanV3EducationPage(),
    updateTreatmentPlanV3EducationInfo: updateTreatmentPlanV3EducationInfo(),
    setTreatmentPlanV3NotAttendingSchool: setTreatmentPlanV3NotAttendingSchool(),
  };
}

function shouldFetchEducationPage(
  { treatmentPlanId }: GetTreatmentPlanV3EducationPagePayload,
  { getState }: { getState: () => TreatmentPlanV3EducationPageThunkConfig['state'] },
) {
  const { educationPage, educationPageLoading, educationPageTreatmentPlanId } =
    getState().treatmentPlanV3;
  if (educationPageTreatmentPlanId !== treatmentPlanId) return true;
  return educationPage == null && !educationPageLoading;
}

function getTreatmentPlanV3EducationPage() {
  return createAsyncThunk<
    ITreatmentPlanV3EducationPage,
    GetTreatmentPlanV3EducationPagePayload,
    TreatmentPlanV3EducationPageThunkConfig
  >(
    'treatmentPlanV3/getTreatmentPlanV3EducationPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      toTreatmentPlanV3EducationPage(
        await fetchWrapper.get({
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/education/`,
          getAccessTokenSilently,
        }),
      ),
    { condition: shouldFetchEducationPage },
  );
}

function updateTreatmentPlanV3EducationInfo() {
  return createAsyncThunk<ITreatmentPlanV3EducationPage, UpdateTreatmentPlanV3EducationInfoPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3EducationInfo',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      toTreatmentPlanV3EducationPage(
        await fetchWrapper.patch(
          {
            url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/education/`,
            getAccessTokenSilently,
          },
          toTreatmentPlanV3EducationInfoRequest(data),
        ),
      ),
  );
}

function setTreatmentPlanV3NotAttendingSchool() {
  return createAsyncThunk<
    ITreatmentPlanV3EducationPage,
    SetTreatmentPlanV3NotAttendingSchoolPayload
  >(
    'treatmentPlanV3/setTreatmentPlanV3NotAttendingSchool',
    async ({ treatmentPlanId, notAttendingSchool, getAccessTokenSilently }) =>
      toTreatmentPlanV3EducationPage(
        await fetchWrapper.patch(
          {
            url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/education/`,
            getAccessTokenSilently,
          },
          { not_attending_school: notAttendingSchool },
        ),
      ),
  );
}
